//./react-app/src/scenes/dashboard/index.tsx
import Header from '../../components/Header';
import { FC, useState, useContext, useEffect } from 'react';
import { Box, Typography, useTheme, Button } from '@mui/material';
import { tokens } from '../../theme';
import DailyControllingChart from '../../components/DailyControllingChart';
import DailyControllingCustomerFilter, {
  customerOptions,
} from '../../components/DailyControllingCustomerFilter';
import DailyControllingClientFilter from '../../components/DailyControllingClientFilter';
import LeadSourceChart from '../../components/LeadSourceChart';
import CustomerRadioFilter from '../../components/CustomerRadioFilter';
import DailyControllingKPICards from '../../components/DailyControllingKPICards';
import TopClientsPerformanceTable from '../../components/TopClientsPerformanceTable';
import TopSourcesPerformanceTable from '../../components/TopSourcesPerformanceTable';
import LeadStatusStackedChart from '../../components/LeadStatusStackedChart';
import LeadGenerationHeatmap from '../../components/LeadGenerationHeatmap';
import { FilterContext } from '../../contexts/FilterContext';
import moment from 'moment';

const Dashboard: FC = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const { filterState, setFilterState } = useContext(FilterContext)!;

  // State for customer selection (multi-select) - default to all customers selected
  const [selectedCustomers, setSelectedCustomers] = useState<string[]>(
    customerOptions.map((option) => option.value),
  );

  // State for client selection - will be populated after fetching client data
  const [selectedClients, setSelectedClients] = useState<number[]>([]);

  // State for source chart customer selection (single select) - default to FT
  const [sourceCustomer, setSourceCustomer] = useState<string>('ft');

  const handleCustomerSelectionChange = (customers: string[]) => {
    setSelectedCustomers(customers);
  };

  const handleClientSelectionChange = (clients: number[]) => {
    setSelectedClients(clients);
  };

  const handleSourceCustomerChange = (customer: string) => {
    setSourceCustomer(customer);
  };

  const setDateRange = (amount: number, unit: 'week' | 'weeks' | 'month' | 'months' | 'year') => {
    const today = moment().endOf('day').toDate();
    const startDate = moment().subtract(amount, unit).startOf('day').toDate();
    setFilterState((prev) => ({
      ...prev,
      startDate,
      endDate: today,
    }));
  };

  useEffect(() => {
    const handleKeyPress = (event: KeyboardEvent) => {
      if (event.target instanceof HTMLInputElement || event.target instanceof HTMLTextAreaElement) {
        return;
      }

      switch (event.key) {
        case '1':
          setDateRange(1, 'week');
          break;
        case '2':
          setDateRange(2, 'weeks');
          break;
        case '3':
          setDateRange(1, 'month');
          break;
        case '4':
          setDateRange(3, 'months');
          break;
        case '5':
          setDateRange(1, 'year');
          break;
        default:
          return;
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [setFilterState]);

  return (
    <Box m="20px">
      <Box justifyContent="space-between" alignItems="center" display="flex">
        <Header title="Dashboard" subtitle="Lead-Generierung Entwicklung nach Clients" />
        <Box display="flex" gap={2} alignItems="center">
          <Box display="flex" gap={1}>
            <Button
              variant="outlined"
              size="small"
              onClick={() => setDateRange(1, 'week')}
              sx={{ minWidth: 'auto', px: 1.5 }}
            >
              1W
            </Button>
            <Button
              variant="outlined"
              size="small"
              onClick={() => setDateRange(2, 'weeks')}
              sx={{ minWidth: 'auto', px: 1.5 }}
            >
              2W
            </Button>
            <Button
              variant="outlined"
              size="small"
              onClick={() => setDateRange(1, 'month')}
              sx={{ minWidth: 'auto', px: 1.5 }}
            >
              1M
            </Button>
            <Button
              variant="outlined"
              size="small"
              onClick={() => setDateRange(3, 'months')}
              sx={{ minWidth: 'auto', px: 1.5 }}
            >
              3M
            </Button>
            <Button
              variant="outlined"
              size="small"
              onClick={() => setDateRange(1, 'year')}
              sx={{ minWidth: 'auto', px: 1.5 }}
            >
              1J
            </Button>
          </Box>
          <DailyControllingCustomerFilter
            selectedCustomers={selectedCustomers}
            onSelectionChange={handleCustomerSelectionChange}
          />
          <DailyControllingClientFilter
            selectedClients={selectedClients}
            onSelectionChange={handleClientSelectionChange}
            selectedCustomers={selectedCustomers}
            startDate={filterState.startDate || undefined}
            endDate={filterState.endDate || undefined}
          />
        </Box>
      </Box>

      {/* KPI Cards */}
      <DailyControllingKPICards
        selectedCustomers={selectedCustomers}
        selectedClients={selectedClients}
      />

      <Box display="grid" gridTemplateColumns="1fr" gap="20px">
        {/* Client-based chart */}
        <Box sx={{ backgroundColor: colors.primary[400], borderRadius: '8px' }}>
          <Typography variant="h5" fontWeight="600" sx={{ padding: '30px 30px 0 30px' }}>
            Daily Controlling - Lead-Entwicklung nach Clients
          </Typography>
          <Box height="500px" mt="-20px" p="0 30px 30px 30px">
            <DailyControllingChart
              selectedCustomers={selectedCustomers}
              selectedClients={selectedClients}
            />
          </Box>
        </Box>

        {/* Source-based chart */}
        <Box sx={{ backgroundColor: colors.primary[400], borderRadius: '8px' }}>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            sx={{ padding: '30px 30px 0 30px' }}
          >
            <Typography variant="h5" fontWeight="600">
              Lead-Entwicklung nach Source
            </Typography>
            <CustomerRadioFilter
              selectedCustomer={sourceCustomer}
              onSelectionChange={handleSourceCustomerChange}
            />
          </Box>
          <Box height="500px" mt="-20px" p="0 30px 30px 30px">
            <LeadSourceChart selectedCustomer={sourceCustomer} />
          </Box>
        </Box>

        {/* Performance Tables */}
        <Box display="grid" gridTemplateColumns="1fr 1fr" gap="20px">
          <Box sx={{ backgroundColor: colors.primary[400], borderRadius: '8px', p: 3 }}>
            <TopClientsPerformanceTable
              selectedCustomers={selectedCustomers}
              selectedClients={selectedClients}
            />
          </Box>
          <Box sx={{ backgroundColor: colors.primary[400], borderRadius: '8px', p: 3 }}>
            <TopSourcesPerformanceTable selectedCustomers={selectedCustomers} />
          </Box>
        </Box>

        {/* Lead Status Stacked Chart */}
        <Box sx={{ backgroundColor: colors.primary[400], borderRadius: '8px' }}>
          <Typography variant="h5" fontWeight="600" sx={{ padding: '30px 30px 0 30px' }}>
            Lead-Status Entwicklung
          </Typography>
          <Box p="0 30px 30px 30px">
            <LeadStatusStackedChart
              selectedCustomers={selectedCustomers}
              selectedClients={selectedClients}
            />
          </Box>
        </Box>

        {/* Calendar Heatmap */}
        <Box sx={{ backgroundColor: colors.primary[400], borderRadius: '8px' }}>
          <Typography variant="h5" fontWeight="600" sx={{ padding: '30px 30px 0 30px' }}>
            Lead-Generierung Kalender (Neue Leads)
          </Typography>
          <Box p="0 30px 30px 30px">
            <LeadGenerationHeatmap
              selectedCustomers={selectedCustomers}
              selectedClients={selectedClients}
            />
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default Dashboard;
